import { ComponentPropsWithoutRef, FC, ReactElement, useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import Button from "./Button";
import Spinner from "./Spinner";
import Typography from "./Typography";

type ConfirmActionProps = {
  leading?: ReactElement;
  title?: string | ReactElement;
  description?: string | ReactElement | null;
  centerText?: boolean;
  caption?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmLoading?: boolean;
  contentLoading?: boolean;
  buttonContent?: ReactElement;
  footerLeading?: ReactElement;
  onConfirm?: () => void;
  onCancel?: () => void;
  // Omit the native `title` attribute so the dialog title can be a ReactElement.
} & Omit<ComponentPropsWithoutRef<"div">, "title">;

const ConfirmAction: FC<ConfirmActionProps> = ({
  leading,
  title,
  description,
  centerText = false,
  onConfirm,
  onCancel,
  confirmLoading,
  contentLoading,
  caption,
  confirmLabel,
  cancelLabel,
  buttonContent,
  footerLeading,
  ...rest
}) => {
  const handleCancel = useCallback(() => {
    if (confirmLoading) return;
    onCancel?.();
  }, [confirmLoading, onCancel]);
  const handleConfirm = useCallback(() => {
    if (confirmLoading || contentLoading) return;
    onConfirm?.();
  }, [confirmLoading, contentLoading, onConfirm]);

  useHotkeys(
    "esc",
    (e) => {
      e.preventDefault();
      handleCancel?.();
    },
    [handleCancel],
    { enableOnContentEditable: true },
  );

  useHotkeys(
    "enter",
    (e) => {
      e.preventDefault();
      handleConfirm?.();
    },
    [handleConfirm],
    { enableOnContentEditable: false, preventDefault: true },
  );
  const ownsDialogClose = Boolean(onCancel) && !contentLoading;

  return (
    <div
      // The Cancel button below IS the way out of this dialog, so the
      // surrounding Dialog's own close X — a second control for the same
      // action, and a dead header row above the title on mobile — stands
      // down (see `CONTENT_OWNS_CLOSE` in components/Dialog.tsx).
      // While `contentLoading` the buttons are a spinner: nothing to own.
      // Declared BEFORE the spread, so a call site overrides it either way —
      // and one has to when its cancel is not really a dismissal. The
      // "Export without watermark?" dialogs are the live example: their
      // "Continue with watermark" cancel starts the export, so both buttons
      // act, nothing dismisses, and they pass `undefined` to keep the X.
      data-owns-dialog-close={ownsDialogClose ? "" : undefined}
      {...rest}
      className={`${rest.className} flex flex-col text-start`}
    >
      {leading}
      <Typography variant="h5" className={centerText ? "text-center" : ""}>
        {title ?? " Are you sure you want to delete this video?"}
      </Typography>

      {description !== null && <Typography
        variant="body2"
        className={`mt-2 text-foreground-secondary ${centerText ? "text-center" : ""}`}
      >
        {description ?? "This action cannot be undone."}
      </Typography>}

      {!contentLoading && rest.children}
      {caption && (
        <Typography variant="caption" className="mt-3 text-foreground-muted">
          {caption}
        </Typography>
      )}

      <div className="flex w-full flex-wrap items-center gap-3 pt-6">
        {!contentLoading && footerLeading}
        <div
          className={`flex items-end justify-end gap-2 ${
            footerLeading && !contentLoading
              ? "ml-auto flex-row"
              : "w-full flex-col-reverse md:flex-row"
          }`}
        >
          {contentLoading ? (
            <Spinner className="mx-auto w-6 h-6" />
          ) : (
            <>
              {onCancel && (
                <Button
                  type="button"
                  disabled={confirmLoading}
                  onClick={handleCancel}
                  size="m"
                  className={footerLeading ? "w-auto" : "w-full md:w-auto"}
                  variant="secondary"
                >
                  {cancelLabel ?? "Cancel"}
                </Button>
              )}
              {buttonContent}

              {onConfirm && (
                <Button
                  type="button"
                  autoFocus={!confirmLoading && !contentLoading}
                  disabled={confirmLoading}
                  onClick={handleConfirm}
                  size="m"
                  loading={confirmLoading}
                  className={footerLeading ? "w-auto" : "w-full md:w-auto"}
                >
                  {confirmLabel ?? "Confirm"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmAction;
